import axios from 'axios';
import React, {useContext, useEffect, useState} from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faServer, faClock, faCalendarAlt, faEdit, faSave, faTimes, faCheck } from '@fortawesome/free-solid-svg-icons';
import {CoinContext} from "../../context/CoinContext";

interface HostingData {
    workerId: string;
    hostingCost: number | null;
}

interface HostingReport {
    workerId: string;
    hostingCost: number | null;
    uptimeInHours: number | null;
    date: string | null;
}

interface EditableHostingData extends HostingData {
    selected: boolean;
    editedCost: string;
}

interface UserInfo {
    userName: string;
    email: string;
    type: string;
}

function Hosting() {
    const [hostingList, setHostingList] = useState<HostingData[]>([]);
    const [hostingReports, setHostingReports] = useState<HostingReport[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [reportLoading, setReportLoading] = useState<boolean>(false);
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [error, setError] = useState<string>('');
    const [isAdmin, setIsAdmin] = useState<boolean>(false);
    const [editMode, setEditMode] = useState<boolean>(false);
    const [editableHostingList, setEditableHostingList] = useState<EditableHostingData[]>([]);
    const [updateLoading, setUpdateLoading] = useState<boolean>(false);
    const [successMessage, setSuccessMessage] = useState<string>('');
    const coinContext = useContext(CoinContext);

    // Fetch hosting data and check admin status on component mount
    useEffect(() => {
        fetchHostingData();
        checkAdminStatus();
    }, [coinContext]);

    const checkAdminStatus = async () => {
        try {
            const response = await axios.get<UserInfo>('/pool/info');
            setIsAdmin(response.data.type === 'ADMIN');
        } catch (error) {
            console.error('Error checking admin status:', error);
            setIsAdmin(false);
        }
    };

    const initializeEditableList = (data: HostingData[]) => {
        const editableData: EditableHostingData[] = data.map(item => ({
            ...item,
            selected: false,
            editedCost: item.hostingCost?.toString() || '0'
        }));
        setEditableHostingList(editableData);
    };

    const fetchHostingData = async () => {
        try {
            setLoading(true);
            setError('');
            if (coinContext?.selectedCoin) {
                const response = await axios.get<HostingData[]>(`/pool/hosting?coin=${coinContext.selectedCoin}`);
                setHostingList(response.data);
                initializeEditableList(response.data);
            }
        } catch (error) {
            console.error('Error fetching hosting data:', error);
            setError('Failed to load hosting data');
        } finally {
            setLoading(false);
        }
    };

    const fetchHostingReport = async () => {
        if (!startDate || !endDate) {
            setError('Please select both start and end dates');
            return;
        }

        try {
            setReportLoading(true);
            setError('');
            if (coinContext?.selectedCoin) {
                const response = await axios.get<HostingReport[]>('/pool/hosting-report', {
                    params: {
                        startDate,
                        endDate,
                        coin: coinContext.selectedCoin
                    }
                });
                setHostingReports(response.data);
            }
        } catch (error) {
            console.error('Error fetching hosting report:', error);
            setError('Failed to load hosting report');
        } finally {
            setReportLoading(false);
        }
    };

    const handleSearch = () => {
        fetchHostingReport();
    };

    const formatDate = (dateString: string | null) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatCurrency = (amount: number | null) => {
        if (amount === null || amount === undefined) return 'N/A';
        return `$${amount.toFixed(4)}`;
    };

    const formatUptime = (uptime: number | null) => {
        if (uptime === null || uptime === undefined) return 'N/A';
        return `${uptime.toFixed(2)}h`;
    };

    const handleEditMode = () => {
        setEditMode(!editMode);
        setError('');
        setSuccessMessage('');
    };

    const handleSelectAll = (checked: boolean) => {
        setEditableHostingList(prev =>
            prev.map(item => ({ ...item, selected: checked }))
        );
    };

    const handleSelectItem = (index: number, checked: boolean) => {
        setEditableHostingList(prev =>
            prev.map((item, i) =>
                i === index ? { ...item, selected: checked } : item
            )
        );
    };

    const handleCostChange = (index: number, value: string) => {
        setEditableHostingList(prev =>
            prev.map((item, i) =>
                i === index ? { ...item, editedCost: value } : item
            )
        );
    };

    const handleUpdateHostingCosts = async () => {
        const selectedItems = editableHostingList.filter(item => item.selected);

        if (selectedItems.length === 0) {
            setError('Please select at least one worker to update');
            return;
        }

        const updateData = selectedItems.map(item => ({
            workerId: item.workerId,
            hostingCost: parseFloat(item.editedCost) || 0
        }));

        try {
            setUpdateLoading(true);
            setError('');
            if (coinContext?.selectedCoin) {
                await axios.post(`/pool/worker/update?coin=${coinContext.selectedCoin}`, updateData);
                setSuccessMessage(`Successfully updated hosting costs for ${selectedItems.length} workers`);
                await fetchHostingData(); // Refresh data
                setEditMode(false);
            }
        } catch (error) {
            console.error('Error updating hosting costs:', error);
            setError('Failed to update hosting costs');
        } finally {
            setUpdateLoading(false);
        }
    };

    const getSelectedCount = () => {
        return editableHostingList.filter(item => item.selected).length;
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center p-8">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
            </div>
        );
    }

    return (
        <div className="w-full max-w-6xl mx-auto p-4 space-y-6">
            {/* Error Message */}
            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                    {error}
                </div>
            )}

            {/* Success Message */}
            {successMessage && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
                    {successMessage}
                </div>
            )}

            {/* Hosting List Section */}
            <div className="bg-white shadow-md rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                    <div className="flex justify-between items-center">
                        <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                            <FontAwesomeIcon icon={faServer} />
                            Hosting Overview
                        </h2>
                        {isAdmin && (
                            <div className="flex items-center gap-2">
                                {editMode && (
                                    <>
                                        <span className="text-sm text-gray-600">
                                            {getSelectedCount()} selected
                                        </span>
                                        <button
                                            onClick={handleUpdateHostingCosts}
                                            disabled={updateLoading}
                                            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center gap-2 text-sm"
                                        >
                                            <FontAwesomeIcon icon={faSave} />
                                            {updateLoading ? 'Updating...' : 'Update Selected'}
                                        </button>
                                    </>
                                )}
                                <button
                                    onClick={handleEditMode}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2 text-sm"
                                >
                                    <FontAwesomeIcon icon={editMode ? faTimes : faEdit} />
                                    {editMode ? 'Cancel' : 'Edit'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
                <div className={`${hostingList.length > 8 ? 'max-h-96 overflow-auto' : 'overflow-x-auto'}`}>
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className={`bg-gray-50 ${hostingList.length > 8 ? 'sticky top-0 z-10' : ''}`}>
                            <tr>
                                {editMode && isAdmin && (
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        <input
                                            type="checkbox"
                                            onChange={(e) => handleSelectAll(e.target.checked)}
                                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                        />
                                    </th>
                                )}
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Worker ID
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Hosting Cost
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {(editMode ? editableHostingList : hostingList).map((item, index) => (
                                <tr key={index} className="hover:bg-gray-50">
                                    {editMode && isAdmin && (
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <input
                                                type="checkbox"
                                                checked={(item as EditableHostingData).selected || false}
                                                onChange={(e) => handleSelectItem(index, e.target.checked)}
                                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                            />
                                        </td>
                                    )}
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {item.workerId?.replace(/#[^.]*\./, '.').split('#')[0] || 'N/A'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {editMode && isAdmin ? (
                                            <input
                                                type="number"
                                                value={(item as EditableHostingData).editedCost || '0'}
                                                onChange={(e) => handleCostChange(index, e.target.value)}
                                                step="0.01"
                                                min="0"
                                                className="w-24 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            />
                                        ) : (
                                            <span className="font-semibold text-green-600">
                                                {formatCurrency(item.hostingCost)}
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {hostingList.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                            No hosting data available
                        </div>
                    )}
                </div>
            </div>

            {/* Date Filter Section */}
            <div className="bg-white shadow-md rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                        <FontAwesomeIcon icon={faCalendarAlt} />
                        Hosting Report
                    </h2>
                </div>
                <div className="p-6">
                    <div className="flex flex-col sm:flex-row gap-4 items-end">
                        <div className="flex-1">
                            <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-2">
                                Start Date
                            </label>
                            <input
                                type="date"
                                id="startDate"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        <div className="flex-1">
                            <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-2">
                                End Date
                            </label>
                            <input
                                type="date"
                                id="endDate"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        <button
                            onClick={handleSearch}
                            disabled={reportLoading}
                            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            <FontAwesomeIcon icon={faSearch} />
                            {reportLoading ? 'Searching...' : 'Search'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Hosting Report Section */}
            {hostingReports.length > 0 && (
                <div className="bg-white shadow-md rounded-lg overflow-hidden">
                    <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                        <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                            <FontAwesomeIcon icon={faClock} />
                            Hosting Report ({startDate} to {endDate})
                        </h2>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Worker ID
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Hosting Cost
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Uptime (Hours)
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Date
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {hostingReports.map((report, index) => (
                                    <tr key={index} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {report.workerId?.replace(/#[^.]*\./, '.').split('#')[0] || 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-semibold text-green-600">
                                            {formatCurrency(report.hostingCost)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {formatUptime(report.uptimeInHours)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {formatDate(report.date)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {reportLoading && (
                <div className="flex justify-center items-center p-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-2 text-gray-600">Loading report...</span>
                </div>
            )}
        </div>
    );
}

export default Hosting;
